/**
 * FETBetaResultSeries
 *
 * What the clinic has on file for this cycle's beta: the order's state, every
 * value it has returned so far in sequence, and the clinic's own reading of
 * them.
 *
 * It used to live inside `WaitingPhaseView`, which is no longer rendered on an
 * FET cycle — the Outcome tab keeps one design now, the step's own panel. A
 * single beta reaches that panel through the recorded result, but a series does
 * not: a rising or falling pair is the whole clinical point of repeating the
 * test, and it was only ever drawn here. So the card moved to where the patient
 * now looks instead of going away with the surface that happened to host it.
 *
 * Reads only. Every value here belongs to the clinic, and nothing on this card
 * offers to change one.
 */

import { View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { EMBER } from "../../../../../onboarding/theme";
import { useTranslations, useLocale, type Locale } from "@src/packages/i18n";
import { formatDate, formatTime } from "@src/packages/i18n/formatters";
import type {
  FETBetaLifecycle,
  FETBetaOrder,
} from "@src/packages/cycles/useFETBetaLifecycle";
import {
  getFETBetaOrderDate,
  selectLatestFETBetaOrder,
} from "@src/packages/cycles/useFETBetaLifecycle";

/**
 * A collection day, written out.
 *
 * Built on the shared formatter so the locale mapping lives in one place, and
 * parsed at local midnight — `new Date("2026-08-24")` is UTC midnight, which
 * renders as the day before west of Greenwich.
 */
function formatBetaDay(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const date = new Date(`${iso}T00:00:00`);
  if (isNaN(date.getTime())) return "—";
  return formatDate(date, locale, {
    weekday: "short",
    month: "long",
    day: "numeric",
  });
}

const FET_BETA_STATUS_KEYS: Record<FETBetaOrder["status"], string> = {
  ordered: "care.treatment_hub.waiting_phase.beta_order_status_ordered",
  scheduled: "care.treatment_hub.waiting_phase.beta_order_status_scheduled",
  collected: "care.treatment_hub.waiting_phase.beta_order_status_collected",
  missed: "care.treatment_hub.waiting_phase.beta_order_status_missed",
  cancelled: "care.treatment_hub.waiting_phase.beta_order_status_cancelled",
  changed: "care.treatment_hub.waiting_phase.beta_order_status_changed",
  completed: "care.treatment_hub.waiting_phase.beta_order_status_completed",
  unknown: "care.treatment_hub.waiting_phase.beta_order_status_unknown",
};

type FETBetaInterpretation = FETBetaOrder["interpretations"][number];
type FETBetaResult = FETBetaOrder["results"][number];

function formatBetaResultCollectionDate(
  result: FETBetaResult,
  locale: Locale,
): string {
  if (!result.collectionAt || result.collectionTimeUnknown) {
    return formatBetaDay(
      (result.collectionAt ?? result.resultAt).slice(0, 10),
      locale,
    );
  }
  const collectionDate = new Date(result.collectionAt);
  if (Number.isNaN(collectionDate.getTime())) {
    return formatBetaDay(result.resultAt.slice(0, 10), locale);
  }
  const date = formatDate(collectionDate, locale, {
    weekday: "short",
    month: "long",
    day: "numeric",
  });
  // `formatDate` is `toLocaleDateString`, and the spec adds year/month/day back
  // when the options carry no date field — so asking it for an hour returned
  // "8/24/2026, 5:30 PM" and the row read "Mon, August 24, 8/24/2026, 5:30 PM".
  const time = formatTime(collectionDate, locale, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date}, ${time}`;
}

const FET_BETA_INTERPRETATION_KEYS: Record<
  FETBetaInterpretation["state"],
  string
> = {
  negative:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_negative",
  indeterminate:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_indeterminate",
  pregnancy_detected:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_pregnancy_detected",
  pregnancy_of_unknown_location:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_unknown_location",
  intrauterine_pregnancy:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_intrauterine",
  ectopic_concern:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_ectopic_concern",
  ectopic_diagnosis:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_ectopic_diagnosis",
  biochemical_pregnancy:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_biochemical",
  early_pregnancy_loss:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_early_loss",
  ongoing_pregnancy:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_ongoing",
  live_birth:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_live_birth",
  unavailable:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_unavailable",
  unknown: "care.treatment_hub.waiting_phase.beta_interpretation_state_unknown",
  corrected:
    "care.treatment_hub.waiting_phase.beta_interpretation_state_corrected",
  refuted: "care.treatment_hub.waiting_phase.beta_interpretation_state_refuted",
};

const FET_BETA_FOLLOW_UP_KEYS: Record<
  FETBetaInterpretation["followUpAction"],
  string
> = {
  repeat_beta_hcg: "care.treatment_hub.waiting_phase.beta_follow_up_repeat",
  ultrasound: "care.treatment_hub.waiting_phase.beta_follow_up_ultrasound",
  clinic_review: "care.treatment_hub.waiting_phase.beta_follow_up_review",
  urgent_evaluation: "care.treatment_hub.waiting_phase.beta_follow_up_urgent",
  none: "care.treatment_hub.waiting_phase.beta_follow_up_none",
  unknown: "care.treatment_hub.waiting_phase.beta_follow_up_unknown",
};

function selectCurrentFETBetaInterpretation(
  order: FETBetaOrder | null,
): FETBetaInterpretation | null {
  return (
    [...(order?.interpretations ?? [])]
      .filter(
        (candidate) =>
          candidate.state !== "corrected" && candidate.state !== "refuted",
      )
      .sort((left, right) =>
        right.interpretedAt.localeCompare(left.interpretedAt),
      )[0] ?? null
  );
}

export function FETBetaResultSeries({
  lifecycle,
}: {
  lifecycle: FETBetaLifecycle | null;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const order = selectLatestFETBetaOrder(lifecycle);
  const results = [...(order?.results ?? [])]
    .filter(
      (candidate) =>
        candidate.recordStatus === "current" &&
        candidate.status !== "entered_in_error" &&
        candidate.status !== "refuted",
    )
    .sort((left, right) => left.sequence - right.sequence);
  const interpretation = selectCurrentFETBetaInterpretation(order);
  const orderDate = getFETBetaOrderDate(order);
  const status = order
    ? t(
        FET_BETA_STATUS_KEYS[order.status] ??
          "care.treatment_hub.waiting_phase.beta_order_status_unknown",
      )
    : t("care.treatment_hub.waiting_phase.beta_order_status_not_reported");

  return (
    <View
      testID="fet-beta-order-status"
      style={{
        ...(GLASS_CARD_STYLE as Record<string, unknown>),
        borderRadius: 18,
        padding: 16,
        gap: 8,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 20,
          color: EMBER.colors.text,
          fontWeight: "800",
        }}
      >
        {t("care.treatment_hub.waiting_phase.beta_order_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 13, lineHeight: 19, color: EMBER.colors.textMid }}
      >
        {t("care.treatment_hub.waiting_phase.beta_order_status", {
          params: { status },
        })}
      </Text>
      {orderDate ? (
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, lineHeight: 18, color: EMBER.colors.textMid }}
        >
          {t("care.treatment_hub.waiting_phase.beta_order_collection_window", {
            params: { date: formatBetaDay(orderDate, locale) },
          })}
        </Text>
      ) : null}
      {results.length > 0 ? (
        <View testID="fet-beta-result-series" style={{ gap: 6 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "700",
            }}
          >
            {t("care.treatment_hub.waiting_phase.beta_order_result_series")}
          </Text>
          {results.map((result) => (
            <View key={result.id} style={{ gap: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 18,
                  color: EMBER.colors.textMid,
                }}
              >
                {t("care.treatment_hub.waiting_phase.beta_order_result_entry", {
                  params: {
                    sequence: String(result.sequence),
                    value: result.value == null ? "—" : String(result.value),
                    unit: result.unit ?? "",
                  },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  lineHeight: 16,
                  color: EMBER.colors.textFaint,
                }}
              >
                {t(
                  result.collectionAt
                    ? "care.treatment_hub.waiting_phase.beta_order_result_collected_date"
                    : "care.treatment_hub.waiting_phase.beta_order_result_date",
                  {
                    params: {
                      date: formatBetaResultCollectionDate(result, locale),
                    },
                  },
                )}
              </Text>
            </View>
          ))}
        </View>
      ) : (
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, lineHeight: 18, color: EMBER.colors.textMid }}
        >
          {t(
            order
              ? "care.treatment_hub.waiting_phase.beta_order_no_result"
              : "care.treatment_hub.waiting_phase.beta_order_no_order",
          )}
        </Text>
      )}
      {interpretation ? (
        <View
          testID="fet-beta-clinic-interpretation"
          style={{
            marginTop: 4,
            paddingTop: 10,
            borderTopWidth: 1,
            borderTopColor: "rgba(92,52,32,0.1)",
            gap: 5,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "700",
            }}
          >
            {t("care.treatment_hub.waiting_phase.beta_interpretation_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
            }}
          >
            {t("care.treatment_hub.waiting_phase.beta_interpretation_state", {
              params: {
                state: t(FET_BETA_INTERPRETATION_KEYS[interpretation.state]),
              },
            })}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
            }}
          >
            {t(
              "care.treatment_hub.waiting_phase.beta_interpretation_follow_up",
              {
                params: {
                  action: t(
                    FET_BETA_FOLLOW_UP_KEYS[interpretation.followUpAction],
                  ),
                  date: interpretation.followUpDueAt
                    ? formatBetaDay(
                        interpretation.followUpDueAt.slice(0, 10),
                        locale,
                      )
                    : t(
                        "care.treatment_hub.waiting_phase.beta_interpretation_no_due_date",
                      ),
                },
              },
            )}
          </Text>
          {interpretation.urgentEscalation ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.warm,
                fontWeight: "700",
              }}
            >
              {t("care.treatment_hub.waiting_phase.beta_interpretation_urgent")}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
